import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banknote, FileCheck2, AlertTriangle, CheckCircle2, Info, XCircle, ChevronDown, ChevronRight, Save } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import retailerSettlement from '../../services/retailerSettlement';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { errorMessage, unwrap } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { money, pct } from '../../utils/nurseryMoney';
import { ReasonDialog, SeatRequired } from '../../components/nursery/accounts/AccountBits';
import {
  CLAIM_TREATMENT_LABELS,
  type ClaimPreviewOk,
  type ClaimsPreview,
  type RecordedClaim,
  type RecordedClaimDetail,
  type RecordedRemittance,
  type RecordedRemittanceDetail,
  type RemittancePreview,
  type RemittancePreviewOk,
  type RemittanceXeroSettings,
  type SettlementFile,
  type XeroSyncStatus,
  type SettlementIssue,
} from '../../types/retailerSettlement';

/**
 * /accounts/remittances — Bunnings' rebate claims and remittance advices.
 *
 * Each month Bunnings claims its rebates (one CSV per claim, listing every
 * invoice); each week a remittance advice pays invoices at full value, and the
 * rebate on those invoices comes off what reaches the bank. The month's claims
 * are recorded first. A remittance is then checked against them and recorded
 * as one payment across its invoices, with each invoice's share of each claim
 * settled out of it.
 *
 * The server judges every file; this page shows what it said. Any Accounts
 * seat checks and reads; recording and voiding need the Accounts Admin seat.
 * Nothing goes to Xero yet (phase 3 of docs/bunnings-remittance-plan.md).
 */

type Tab = 'claims' | 'remittance';

/** A file's bytes as base64, for a JSON request. */
function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || '');
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('The file could not be read'));
    reader.readAsDataURL(file);
  });
}

async function readTexts(files: File[]): Promise<SettlementFile[]> {
  return Promise.all(files.map(async (f) => ({ file_name: f.name, text: await f.text() })));
}

function Issues({ issues, tone }: { issues: SettlementIssue[]; tone: 'red' | 'amber' | 'neutral' | 'green' }) {
  if (!issues.length) return null;
  const cls = {
    red: 'bg-red-50 border-red-200 text-red-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    neutral: 'bg-neutral-50 border-neutral-200 text-neutral-700',
    green: 'bg-green-50 border-green-200 text-green-800',
  }[tone];
  const Icon = tone === 'red' ? XCircle : tone === 'amber' ? AlertTriangle : tone === 'green' ? CheckCircle2 : Info;
  return (
    <ul className={cn('border rounded-lg p-3 space-y-1 text-sm', cls)}>
      {issues.map((i, k) => (
        <li key={`${i.code}-${k}`} className="flex gap-2 min-w-0">
          <Icon className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="break-words min-w-0">{i.message}</span>
        </li>
      ))}
    </ul>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-3 min-w-0">
      <div className="text-xs text-neutral-500 break-words">{label}</div>
      <div className={cn('text-lg font-semibold break-words',
        tone === 'good' ? 'text-green-700' : tone === 'bad' ? 'text-red-700' : 'text-neutral-900')}>
        {value}
      </div>
    </div>
  );
}

/** Where a recorded claim or remittance stands with Xero. Nothing when it is not for Xero. */
function XeroState({ status, error, number }: { status: XeroSyncStatus | null; error: string | null; number?: string | null }) {
  if (!status) return null;
  const text = status === 'synced' ? (number ? `In Xero as ${number}` : 'In Xero')
    : status === 'failed' ? 'Not sent to Xero' : 'On its way to Xero';
  return (
    <div className={cn('text-xs break-words', status === 'failed' ? 'text-red-700' : status === 'synced' ? 'text-green-700' : 'text-neutral-500')}>
      {text}
      {status === 'failed' && error && <div className="text-red-700">{error}</div>}
    </div>
  );
}

const th = 'py-1.5 pr-3 font-medium';
const thr = 'py-1.5 pr-3 font-medium text-right';
const td = 'py-1.5 pr-3';
const tdr = 'py-1.5 pr-3 text-right';
const fileInputCls = 'block w-full text-sm text-neutral-700 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-primary-700';

function Remittances() {
  const tz = useOrgTimezone();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'remittance' ? 'remittance' : 'claims';
  const { hasAccess: canRead, loading: licenseLoading } = useModuleLicense('accounts', ['user', 'admin']);
  const { hasAccess: isAdmin } = useModuleLicense('accounts', 'admin');

  const [claimFiles, setClaimFiles] = useState<File[]>([]);
  const [claimsPreview, setClaimsPreview] = useState<ClaimsPreview | null>(null);
  const [remitFile, setRemitFile] = useState<File | null>(null);
  const [remitPreview, setRemitPreview] = useState<RemittancePreview | null>(null);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [claims, setClaims] = useState<RecordedClaim[]>([]);
  const [remittances, setRemittances] = useState<RecordedRemittance[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [openClaim, setOpenClaim] = useState<RecordedClaimDetail | null>(null);
  const [openRemittance, setOpenRemittance] = useState<RecordedRemittanceDetail | null>(null);
  const [voidingClaim, setVoidingClaim] = useState<RecordedClaim | null>(null);
  const [voidingRemittance, setVoidingRemittance] = useState<RecordedRemittance | null>(null);
  const [xeroSettings, setXeroSettings] = useState<RemittanceXeroSettings | null>(null);
  const [xeroToDo, setXeroToDo] = useState<string[]>([]);

  const fmt = (d: string | null | undefined) => (d ? formatLocalDateInTz(String(d).slice(0, 10), tz) : '');
  const monthOf = (d: string) => formatLocalDateInTz(String(d).slice(0, 10), tz, { month: 'long', year: 'numeric' });

  const loadLists = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([retailerSettlement.listClaims(), retailerSettlement.listRemittances()]);
      setClaims(unwrap(c, 'Could not load the claims'));
      setRemittances(unwrap(r, 'Could not load the remittances'));
      setListError(null);
    } catch (e) {
      setListError(errorMessage(e, 'Could not load what is recorded'));
    }
  }, []);

  useEffect(() => {
    if (canRead) void loadLists();
  }, [canRead, loadLists]);

  // Whether claims and remittances go to Xero: only the offer to send one.
  useEffect(() => {
    if (!canRead) return;
    retailerSettlement.getXeroSettings()
      .then((res) => { if (res.success && res.data) setXeroSettings(res.data); })
      .catch(() => { /* the lists still work without it */ });
  }, [canRead]);

  const canSend = isAdmin && !!xeroSettings?.enabled && xeroSettings.can_post;
  const sendClaim = (c: RecordedClaim) => run('Could not send the claim to Xero', async () => {
    unwrap(await retailerSettlement.sendClaimToXero(c.id), 'Could not send the claim to Xero');
    setNotice(`Claim ${c.claim_number} is on its way to Xero.`);
    await loadLists();
  });
  const sendRemittance = (r: RecordedRemittance) => run('Could not send the remittance to Xero', async () => {
    unwrap(await retailerSettlement.sendRemittanceToXero(r.id), 'Could not send the remittance to Xero');
    setNotice(`Payment ${r.payment_number} is on its way to Xero.`);
    await loadLists();
  });

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === 'claims') next.delete('tab'); else next.set('tab', t);
    setParams(next, { replace: true });
    setError(null);
    setNotice(null);
  };

  const run = async (fallback: string, fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const checkClaims = () => run('Could not read the claims', async () => {
    setClaimsPreview(unwrap(await retailerSettlement.previewClaims(await readTexts(claimFiles)), 'Could not read the claims'));
  });

  const recordClaims = () => run('Could not record the claims', async () => {
    const res = unwrap(await retailerSettlement.recordClaims(await readTexts(claimFiles)), 'Could not record the claims');
    setClaimsPreview(null);
    setClaimFiles([]);
    setNotice(`Recorded ${res.recorded} claim${res.recorded === 1 ? '' : 's'}. Their rebate comes off the remittances that pay those invoices.`);
    await loadLists();
  });

  const checkRemittance = () => run('Could not read the remittance', async () => {
    if (!remitFile) return;
    setRemitPreview(unwrap(await retailerSettlement.previewRemittance(remitFile.name, await readBase64(remitFile)), 'Could not read the remittance'));
  });

  const recordRemittance = () => run('Could not record the remittance', async () => {
    if (!remitFile) return;
    const res = unwrap(await retailerSettlement.recordRemittance(remitFile.name, await readBase64(remitFile)), 'Could not record the remittance');
    setRemitPreview(null);
    setRemitFile(null);
    setNotice(`Recorded payment ${res.payment_number}. ${money(res.deposit)} should have reached the bank.`);
    await loadLists();
  });

  const toggleClaim = async (c: RecordedClaim) => {
    if (openClaim?.id === c.id) { setOpenClaim(null); return; }
    try {
      setOpenClaim(unwrap(await retailerSettlement.getClaim(c.id), 'Could not load the claim'));
    } catch (e) {
      setListError(errorMessage(e, 'Could not load the claim'));
    }
  };

  const toggleRemittance = async (r: RecordedRemittance) => {
    if (openRemittance?.id === r.id) { setOpenRemittance(null); return; }
    try {
      setOpenRemittance(unwrap(await retailerSettlement.getRemittance(r.id), 'Could not load the remittance'));
    } catch (e) {
      setListError(errorMessage(e, 'Could not load the remittance'));
    }
  };

  if (licenseLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }
  if (!canRead) return <SeatRequired seat="user" what="The remittances page" />;

  // ── A checked claim ──
  const renderClaim = (c: ClaimPreviewOk) => {
    const lines = onlyIssues ? c.lines.filter((l) => l.issues.length) : c.lines;
    return (
      <div key={`${c.file_name}-${c.claim_number}`} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3 min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-neutral-900 break-words">Claim {c.claim_number}</h3>
            <p className="text-sm text-neutral-500 break-words">
              {c.head_office ? c.head_office.name : 'No head office found'} · {c.supplier_code}
              {c.claim_date && <> · claimed {fmt(c.claim_date)}</>}
              {c.invoice_dates.from && <> · invoices {fmt(c.invoice_dates.from)} to {fmt(c.invoice_dates.to)}</>}
            </p>
            <p className="text-xs text-neutral-400 break-words">{c.file_name}</p>
          </div>
          <span className={cn('text-xs font-medium rounded-full px-2.5 py-1 break-words',
            c.treatment === 'credit_note' ? 'bg-blue-50 text-blue-700'
              : c.treatment === 'bill' ? 'bg-purple-50 text-purple-700' : 'bg-amber-50 text-amber-800')}>
            {c.treatment ? CLAIM_TREATMENT_LABELS[c.treatment] : 'Not known: credit note or bill?'}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b">
                <th className={th}>Rebate</th>
                <th className={thr}>Rate</th>
                <th className={thr}>Rebatable</th>
                <th className={thr}>Ex GST</th>
                <th className={thr}>GST</th>
                <th className="py-1.5 font-medium text-right">Inc GST</th>
              </tr>
            </thead>
            <tbody>
              {c.totals.parts.map((p) => (
                <tr key={p.name} className="border-b border-neutral-100">
                  <td className={td}>{p.name}</td>
                  <td className={tdr}>{pct(p.rate_pct)}</td>
                  <td className={tdr}>{money(p.rebatable_ex_gst)}</td>
                  <td className={tdr}>{money(p.amount_ex_gst)}</td>
                  <td className={tdr}>{money(p.gst)}</td>
                  <td className="py-1.5 text-right">{money(p.amount_inc_gst)}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className={td} colSpan={3}>Total</td>
                <td className={tdr}>{money(c.totals.amount_ex_gst)}</td>
                <td className={tdr}>{money(c.totals.gst)}</td>
                <td className="py-1.5 text-right">{money(c.totals.amount_inc_gst)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Issues issues={c.issues} tone={c.recordable ? 'amber' : 'red'} />

        <p className="text-sm text-neutral-600 break-words">
          {c.counts.lines} invoice{c.counts.lines === 1 ? '' : 's'} claimed, {c.counts.matched} found here
          {c.counts.with_issues ? `, ${c.counts.with_issues} with something to look at` : ''}.
        </p>
        {lines.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b">
                  <th className={th}>Invoice</th>
                  <th className={th}>Store</th>
                  <th className={thr}>Rebatable</th>
                  <th className={thr}>Rebate inc GST</th>
                  <th className="py-1.5 font-medium">Check</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, k) => (
                  <tr key={`${l.invoice_number}-${k}`} className="border-b border-neutral-100 align-top">
                    <td className={cn(td, 'whitespace-nowrap')}>{l.invoice_number}</td>
                    <td className={td}>{l.invoice?.customer_name || l.store_code}</td>
                    <td className={tdr}>{money(l.rebatable_ex_gst)}</td>
                    <td className={tdr}>{money(l.share.amount_inc_gst)}</td>
                    <td className="py-1.5 min-w-[14rem]">
                      {l.issues.length
                        ? l.issues.map((i) => <div key={i.code} className="text-amber-800">{i.message}</div>)
                        : <span className="text-green-700">Matches</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── A checked remittance ──
  const renderRemittance = (r: RemittancePreviewOk) => {
    const lines = onlyIssues ? r.lines.filter((l) => l.issues.length) : r.lines;
    return (
      <div className="space-y-4">
        <div className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0">
          <h3 className="font-semibold text-neutral-900 break-words">Payment {r.payment_number || '(no number)'}</h3>
          <p className="text-sm text-neutral-500 break-words">
            {r.head_office ? r.head_office.name : 'No head office found'} · {r.creditor_code}
            {r.payment_date && <> · {fmt(r.payment_date)}</>}
          </p>
          <p className="text-xs text-neutral-400 break-words">{r.file_name}</p>
        </div>

        <Issues issues={r.blocks} tone="red" />
        <Issues issues={r.notes} tone="neutral" />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile label="Remittance total" value={money(r.totals.remittance_total ?? r.totals.lines_paid)} />
          <Tile label="Rebate taken off" value={money(r.totals.rebate_inc_gst)} />
          <Tile label="Deposit to expect" value={money(r.totals.deposit)} tone={r.ready ? 'good' : undefined} />
          <Tile label="Lines held" value={`${r.totals.held_count} · ${money(r.totals.held_amount)}`} tone={r.totals.held_count ? 'bad' : undefined} />
        </div>

        <div className={cn('flex items-start gap-2 rounded-lg border p-3 text-sm',
          r.ready ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-900')}>
          {r.ready ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /> : <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />}
          <span className="break-words min-w-0">
            {r.ready
              ? `Ready to record. ${money(r.totals.deposit)} should have reached the bank.${r.totals.short_count
                ? ` ${r.totals.short_count} invoice${r.totals.short_count === 1 ? ' is' : 's are'} paid short and will be left part paid, listed under Short payments.`
                : ''}`
              : 'Not ready: put right what is held below, and check again.'}
          </span>
        </div>

        {r.claims.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0">
            <h4 className="text-sm font-semibold text-neutral-800 mb-2">Claims settled here</h4>
            <ul className="text-sm space-y-1">
              {r.claims.map((c) => (
                <li key={c.claim_number} className="break-words">
                  {c.claim_number}: {money(c.taken_here_inc_gst)} of {money(c.claim_total_inc_gst)}
                  {c.deducted_as_line ? ' (a line of its own)' : ` across ${c.invoices_here} invoice${c.invoices_here === 1 ? '' : 's'}`}
                  {c.settled_before_inc_gst > 0 && `, ${money(c.settled_before_inc_gst)} settled before`}
                  {c.remainder_here && ', with its rounding remainder'}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b">
                  <th className={th}>Line</th>
                  <th className={th}>Store</th>
                  <th className={thr}>Paid</th>
                  <th className={thr}>Owing here</th>
                  <th className={thr}>Rebate</th>
                  <th className="py-1.5 font-medium">Check</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.row} className="border-b border-neutral-100 align-top">
                    <td className={cn(td, 'whitespace-nowrap')}>
                      {l.document_number}
                      {l.kind !== 'invoice' && <div className="text-xs text-neutral-500">{l.type}</div>}
                    </td>
                    <td className={td}>{l.invoice?.customer_name || l.store_name}</td>
                    <td className={tdr}>{money(l.amount_paid)}</td>
                    <td className={tdr}>{l.invoice ? money(l.invoice.balance) : ''}</td>
                    <td className={tdr}>{l.rebate ? money(l.rebate.amount_inc_gst) : ''}</td>
                    <td className="py-1.5 min-w-[14rem]">
                      {l.issues.length
                        ? l.issues.map((i) => (
                          <div key={i.code} className={l.status === 'short' ? 'text-amber-800' : 'text-red-700'}>{i.message}</div>
                        ))
                        : <span className="text-green-700">{l.credit_note ? `Matches ${l.credit_note.credit_note_number}` : 'Matches'}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ── What is recorded ──
  const recordedClaims = (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0 space-y-2">
      <h3 className="font-semibold text-neutral-900">Recorded claims</h3>
      {!claims.length ? (
        <p className="text-sm text-neutral-500">No claims are recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b">
                <th className={th}>Claim</th>
                <th className={th}>Month</th>
                <th className={th}>Head office</th>
                <th className={thr}>Claimed</th>
                <th className={thr}>Settled</th>
                <th className={thr}>Still to come off</th>
                <th className="py-1.5" />
              </tr>
            </thead>
            <tbody>
              {claims.map((c) => (
                <Fragment key={c.id}>
                  <tr className="border-b border-neutral-100 align-top">
                    <td className={cn(td, 'whitespace-nowrap')}>
                      <button type="button" className="inline-flex items-center gap-1 text-primary-700 font-medium" onClick={() => void toggleClaim(c)}
                        aria-expanded={openClaim?.id === c.id}>
                        {openClaim?.id === c.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        {c.claim_number}
                      </button>
                      <div className="text-xs text-neutral-500">{c.treatment === 'credit_note' ? 'Credit note' : 'Bill from Bunnings'}</div>
                      <XeroState status={c.xero_sync_status} error={c.xero_sync_error} number={c.xero_doc_number} />
                      {c.treatment === 'credit_note' && c.xero_doc_number && (
                        <div className="text-xs text-neutral-500 whitespace-normal break-words max-w-[14rem]">
                          Supplier Credit Note on Bunnings&rsquo; portal: {c.xero_doc_number}
                        </div>
                      )}
                    </td>
                    <td className={cn(td, 'whitespace-nowrap')}>{monthOf(c.period_month)}</td>
                    <td className={td}>{c.head_office_name}</td>
                    <td className={tdr}>{money(c.amount_inc_gst)}</td>
                    <td className={tdr}>{money(c.settled_inc_gst)}</td>
                    <td className={tdr}>{money(c.remaining_inc_gst)}</td>
                    <td className="py-1.5 text-right">
                      <div className="flex flex-col items-end gap-1">
                        {canSend && (!c.xero_sync_status || c.xero_sync_status === 'failed') && (
                          <Button size="sm" variant="outline" onClick={() => void sendClaim(c)} disabled={busy}>
                            {c.xero_sync_status === 'failed' ? 'Try again' : 'Send to Xero'}
                          </Button>
                        )}
                        {isAdmin && (
                          <Button size="sm" variant="outline" onClick={() => setVoidingClaim(c)}>Void</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {openClaim?.id === c.id && (
                    <tr className="border-b border-neutral-100">
                      <td colSpan={7} className="py-2">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="text-left text-neutral-500">
                              <th className={th}>Invoice</th>
                              <th className={thr}>Rebatable</th>
                              <th className={thr}>Share inc GST</th>
                              <th className={th}>Settled on</th>
                            </tr>
                          </thead>
                          <tbody>
                            {openClaim.lines.map((l) => (
                              <tr key={l.id}>
                                <td className={cn(td, 'whitespace-nowrap')}>
                                  {l.invoice_id
                                    ? <Link className="text-primary-700" to={`/accounts/invoices/${l.invoice_id}`}>{l.our_invoice_number || l.invoice_number}</Link>
                                    : <span>{l.invoice_number} <span className="text-amber-700">(not found here)</span></span>}
                                </td>
                                <td className={tdr}>{money(l.rebatable_ex_gst)}</td>
                                <td className={tdr}>{money(l.share_inc_gst)}</td>
                                <td className={td}>{l.settled_on ? `Payment ${l.settled_on}` : 'Not yet'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const recordedRemittances = (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0 space-y-2">
      <h3 className="font-semibold text-neutral-900">Recorded remittances</h3>
      {!remittances.length ? (
        <p className="text-sm text-neutral-500">No remittances are recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b">
                <th className={th}>Payment</th>
                <th className={th}>Date</th>
                <th className={th}>Head office</th>
                <th className={thr}>Remittance</th>
                <th className={thr}>Rebate</th>
                <th className={thr}>Deposit</th>
                <th className="py-1.5" />
              </tr>
            </thead>
            <tbody>
              {remittances.map((r) => (
                <Fragment key={r.id}>
                  <tr className="border-b border-neutral-100 align-top">
                    <td className={cn(td, 'whitespace-nowrap')}>
                      <button type="button" className="inline-flex items-center gap-1 text-primary-700 font-medium" onClick={() => void toggleRemittance(r)}
                        aria-expanded={openRemittance?.id === r.id}>
                        {openRemittance?.id === r.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        {r.payment_number}
                      </button>
                      {r.short_count > 0 && <div className="text-xs text-amber-700">{r.short_count} short</div>}
                      <XeroState status={r.xero_sync_status} error={r.xero_sync_error} />
                    </td>
                    <td className={cn(td, 'whitespace-nowrap')}>{fmt(r.payment_date)}</td>
                    <td className={td}>{r.head_office_name}</td>
                    <td className={tdr}>{money(r.lines_paid)}</td>
                    <td className={tdr}>{money(r.rebate_inc_gst)}</td>
                    <td className={cn(tdr, 'font-semibold')}>{money(r.deposit)}</td>
                    <td className="py-1.5 text-right">
                      <div className="flex flex-col items-end gap-1">
                        {canSend && (!r.xero_sync_status || r.xero_sync_status === 'failed') && (
                          <Button size="sm" variant="outline" onClick={() => void sendRemittance(r)} disabled={busy}>
                            {r.xero_sync_status === 'failed' ? 'Try again' : 'Send to Xero'}
                          </Button>
                        )}
                        {isAdmin && (
                          <Button size="sm" variant="outline" onClick={() => setVoidingRemittance(r)}>Void</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {openRemittance?.id === r.id && (
                    <tr className="border-b border-neutral-100">
                      <td colSpan={7} className="py-2 space-y-2">
                        {openRemittance.claims.length > 0 && (
                          <p className="text-xs text-neutral-600 break-words">
                            Claims settled: {openRemittance.claims.map((c) => `${c.claim_number} ${money(c.amount_inc_gst)}`).join(', ')}
                          </p>
                        )}
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="text-left text-neutral-500">
                              <th className={th}>Line</th>
                              <th className={thr}>Paid</th>
                              <th className={thr}>Allocated</th>
                              <th className={thr}>Rebate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {openRemittance.lines.map((l) => (
                              <tr key={l.id}>
                                <td className={cn(td, 'whitespace-nowrap')}>
                                  {l.invoice_id && l.kind === 'invoice'
                                    ? <Link className="text-primary-700" to={`/accounts/invoices/${l.invoice_id}`}>{l.invoice_number}</Link>
                                    : l.document_number}
                                  {l.short && <span className="text-amber-700"> (short)</span>}
                                </td>
                                <td className={tdr}>{money(l.amount_paid)}</td>
                                <td className={tdr}>{money(l.allocated)}</td>
                                <td className={tdr}>{l.rebate_inc_gst ? money(l.rebate_inc_gst) : ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const claimsRecordable = !!claimsPreview && claimsPreview.claims.length > 0 && claimsPreview.claims.every((c) => c.ok && c.recordable);
  const remitReady = !!remitPreview && remitPreview.ok && remitPreview.ready;

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title="Remittances"
        subtitle="Bunnings' rebate claims and remittance advices, checked against your invoices and recorded."
        icon={<Banknote className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <div className="flex gap-2 border-b border-neutral-200">
          {(['claims', 'remittance'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              aria-current={tab === t ? 'page' : undefined}
              className={cn('px-3 py-2 text-sm font-medium border-b-2 -mb-px',
                tab === t ? 'border-primary-600 text-primary-700' : 'border-transparent text-neutral-500 hover:text-neutral-800')}
            >
              {t === 'claims' ? 'Rebate claims' : 'Remittances'}
            </button>
          ))}
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="break-words min-w-0">
            {tab === 'claims'
              ? 'Record the month\'s claims first: a remittance takes each invoice\'s rebate from them.'
              : xeroSettings?.enabled && xeroSettings.can_post
                ? 'Recording pays the invoices and settles the claims here, and posts them to Xero through the clearing account. Match the transfer to the deposit there.'
                : 'Recording pays the invoices and settles the claims here. They are not posted to Xero (Accounts → Xero, Bunnings remittances): reconcile the deposit there by hand.'}
          </span>
        </div>

        {error && <Issues issues={[{ code: 'ERROR', message: error }]} tone="red" />}
        {notice && <Issues issues={[{ code: 'DONE', message: notice }]} tone="green" />}
        {listError && <Issues issues={[{ code: 'LIST', message: listError }]} tone="red" />}
        {xeroToDo.length > 0 && <Issues issues={xeroToDo.map((m, i) => ({ code: `XERO${i}`, message: m }))} tone="amber" />}

        {tab === 'claims' ? (
          <>
            <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
              <label className="block text-sm font-medium text-neutral-800" htmlFor="claim-files">
                The month's claim CSVs (both: the volume rebate and the marketing and consolidation claim)
              </label>
              <input
                id="claim-files"
                type="file"
                multiple
                accept=".csv,text/csv"
                className={fileInputCls}
                onChange={(e) => { setClaimFiles(Array.from(e.target.files || [])); setClaimsPreview(null); }}
              />
              <div className="flex flex-wrap gap-2">
                <Button onClick={checkClaims} disabled={!claimFiles.length || busy}>
                  <FileCheck2 className="h-4 w-4" /> {busy ? 'Working…' : 'Check claims'}
                </Button>
                {isAdmin && claimsPreview && (
                  <Button onClick={recordClaims} disabled={!claimsRecordable || busy}>
                    <Save className="h-4 w-4" /> Record {claimsPreview.claims.length} claim{claimsPreview.claims.length === 1 ? '' : 's'}
                  </Button>
                )}
              </div>
              {claimsPreview && !isAdmin && (
                <p className="text-sm text-neutral-500">Recording claims needs the Accounts Admin seat.</p>
              )}
              {claimsPreview && isAdmin && !claimsRecordable && (
                <p className="text-sm text-red-700 break-words">Every file must be a claim that can be recorded. Put right what is shown in red, or leave that file out.</p>
              )}
            </div>
            {claimsPreview && (
              <div className="space-y-4">
                {claimsPreview.rates.map((r) => (
                  <Issues
                    key={r.head_office.id}
                    tone={r.matches ? 'neutral' : 'amber'}
                    issues={[{ code: 'RATES', message: r.note || `${r.head_office.name}: Bunnings claims ${pct(r.claimed_pct)}, as the agreement says.` }]}
                  />
                ))}
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} />
                  Only lines with something to look at
                </label>
                {claimsPreview.claims.map((c) => (c.ok
                  ? renderClaim(c)
                  : <Issues key={c.file_name} tone="red" issues={[{ code: 'FILE', message: `${c.file_name}: ${c.error}` }]} />))}
              </div>
            )}
            {recordedClaims}
          </>
        ) : (
          <>
            <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
              <label className="block text-sm font-medium text-neutral-800" htmlFor="remit-file">
                Remittance advice (.xls as emailed, .xlsx or .csv)
              </label>
              <input
                id="remit-file"
                type="file"
                accept=".xls,.xlsx,.csv"
                className={fileInputCls}
                onChange={(e) => { setRemitFile(e.target.files?.[0] || null); setRemitPreview(null); }}
              />
              <div className="flex flex-wrap gap-2">
                <Button onClick={checkRemittance} disabled={!remitFile || busy}>
                  <FileCheck2 className="h-4 w-4" /> {busy ? 'Working…' : 'Check remittance'}
                </Button>
                {isAdmin && remitPreview?.ok && (
                  <Button onClick={recordRemittance} disabled={!remitReady || busy}>
                    <Save className="h-4 w-4" /> Record remittance
                  </Button>
                )}
              </div>
              {remitPreview?.ok && !isAdmin && (
                <p className="text-sm text-neutral-500">Recording a remittance needs the Accounts Admin seat.</p>
              )}
            </div>
            {remitPreview && (
              <div className="space-y-4">
                {remitPreview.ok ? (
                  <>
                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                      <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} />
                      Only lines with something to look at
                    </label>
                    {renderRemittance(remitPreview)}
                  </>
                ) : (
                  <Issues tone="red" issues={[{ code: 'FILE', message: `${remitPreview.file_name}: ${remitPreview.error}` }]} />
                )}
              </div>
            )}
            {recordedRemittances}
          </>
        )}
      </div>

      {voidingClaim && (
        <ReasonDialog
          title={`Void claim ${voidingClaim.claim_number}`}
          body={voidingClaim.xero_doc_number
            ? `The claim stops counting against remittances and the rebate agreement, and ${voidingClaim.xero_doc_number} is voided in Xero, which takes its GST off the BAS. A claim already settled on a remittance cannot be voided until that remittance is.`
            : 'The claim stops counting against remittances and the rebate agreement. A claim already settled on a remittance cannot be voided until that remittance is.'}
          label="Why it is being voided"
          confirmLabel="Void claim"
          danger
          onClose={() => setVoidingClaim(null)}
          onConfirm={async (reason) => {
            unwrap(await retailerSettlement.voidClaim(voidingClaim.id, reason), 'Could not void the claim');
            setVoidingClaim(null);
            setOpenClaim(null);
            await loadLists();
          }}
        />
      )}
      {voidingRemittance && (
        <ReasonDialog
          title={`Void payment ${voidingRemittance.payment_number}`}
          body={voidingRemittance.in_xero
            ? 'Its payment of the invoices is voided, so they owe again, and the claims it settled are open again. Its payments in Xero are deleted; the transfer has to be deleted in Xero by hand, and the page will say which.'
            : 'Its payment of the invoices is voided, so they owe again, and the claims it settled are open again. Record the remittance again if it was right.'}
          label="Why it is being voided"
          confirmLabel="Void remittance"
          danger
          onClose={() => setVoidingRemittance(null)}
          onConfirm={async (reason) => {
            const res = unwrap(await retailerSettlement.voidRemittance(voidingRemittance.id, reason), 'Could not void the remittance');
            setXeroToDo(res.xero_to_do ?? []);
            setVoidingRemittance(null);
            setOpenRemittance(null);
            await loadLists();
          }}
        />
      )}
    </div>
  );
}

export default Remittances;
